// All-materials list rendered inside the merged Inventory panel's left
// column. Owns its pagination footer (5 / 10 / 20 / 50 per page) via
// `useInventoryAllMaterialsPagination`; row presentation matches the
// pre-merge inventory tab byte-for-byte (name + code/unit/category +
// vendor + on-hand + WAC + ships-as block + Edit button).
//
// Pagination bar is rendered both above and below the list (post
// 2026-06-02 polish) so users with longer lists can change page size
// or navigate without scrolling to the bottom.
//
// Bulk-select column (post 2026-06-02 slice): the parent passes
// `selectedIds` + `onToggleSelection` so the checkbox state lives at
// the orchestrator level and survives toolbar re-renders. A null
// `selectedIds` disables the column entirely (current behavior for
// any consumer that doesn't want bulk affordances).
import type { MfgMaterial } from "@/lib/manufacturing/types";
import {
  mfgMsdsStatusChipClass,
  mfgMsdsStatusChipLabel,
  parseMfgMsdsStatus,
} from "@/lib/manufacturing/msdsStatus";
import { FIREFLY_TARGET_ATTR } from "@/lib/firefly/fireflyTargetConvention";
import { perPieceWeightLbs } from "@/lib/manufacturing/receiveQtyConversion";
import { reorderThresholdFromMetadata } from "@/lib/manufacturing/reorderThreshold";
import {
  ALL_MATERIALS_PAGE_SIZES,
  useInventoryAllMaterialsPagination,
  type AllMaterialsPageSize,
} from "./useInventoryAllMaterialsPagination";

type Props = {
  materials: readonly MfgMaterial[];
  busy: boolean;
  onEdit: (material: MfgMaterial) => void;
  /** Open receive/issue/adjust transaction drawer. */
  onOpenTransactions?: (material: MfgMaterial) => void;
  /** MSDS-3: open SDS workspace when status is not not_required. */
  onOpenSds?: (material: MfgMaterial) => void;
  /** Optional. When provided, renders a Delete button on active rows
   *  (asks the parent to open a confirm modal). Inactive rows hide
   *  this button — they get the Restore affordance instead. */
  onRequestDelete?: (material: MfgMaterial) => void;
  /** Optional. When provided, renders a Restore button on inactive
   *  rows (asks the parent to open a confirm modal, or apply
   *  directly — caller decides). */
  onRequestRestore?: (material: MfgMaterial) => void;
  /** Optional. Org admin/owner permanent delete for inactive rows. */
  onRequestPermanentDelete?: (material: MfgMaterial) => void;
  selectedIds?: ReadonlySet<string>;
  onToggleSelection?: (id: string) => void;
  onToggleSelectAllOnPage?: (idsOnPage: string[], selectAll: boolean) => void;
};

type PaginationBarProps = {
  page: number;
  pageCount: number;
  pageSize: AllMaterialsPageSize;
  rangeStart: number;
  rangeEnd: number;
  total: number;
  canPrev: boolean;
  canNext: boolean;
  onSetPage: (next: number) => void;
  onSetPageSize: (next: AllMaterialsPageSize) => void;
  /** "top" loses the upper border so it doesn't double-up against the
      actions row; "bottom" keeps the divider above it. */
  variant: "top" | "bottom";
};

function MaterialsPaginationBar(props: PaginationBarProps) {
  const wrapClass =
    props.variant === "bottom"
      ? "flex flex-wrap items-center justify-between gap-2 border-t border-gray-200 pt-2 dark:border-zinc-700"
      : "flex flex-wrap items-center justify-between gap-2";
  return (
    <div className={wrapClass}>
      <p className="text-[11px] text-gray-600 dark:text-zinc-400">
        Showing{" "}
        <span className="tabular-nums font-medium text-gray-800 dark:text-zinc-200">{props.rangeStart}</span>
        –<span className="tabular-nums font-medium text-gray-800 dark:text-zinc-200">{props.rangeEnd}</span> of{" "}
        <span className="tabular-nums font-medium text-gray-800 dark:text-zinc-200">{props.total}</span>
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <label className="flex items-center gap-1 text-[11px] text-gray-600 dark:text-zinc-400">
          Per page
          <select
            className="rounded-md border border-gray-200 bg-white px-1.5 py-0.5 text-xs text-gray-900 dark:border-zinc-600 dark:bg-zinc-900 dark:text-zinc-100"
            value={props.pageSize}
            onChange={(e) => props.onSetPageSize(Number(e.target.value) as AllMaterialsPageSize)}
          >
            {ALL_MATERIALS_PAGE_SIZES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </label>
        <div className="flex items-center gap-1">
          <button
            type="button"
            disabled={!props.canPrev}
            onClick={() => props.onSetPage(props.page - 1)}
            className="rounded-md border border-gray-200 bg-white px-2 py-0.5 text-[11px] font-medium text-gray-800 disabled:opacity-40 dark:border-zinc-600 dark:bg-zinc-900 dark:text-zinc-100"
          >
            Prev
          </button>
          <span className="px-1 text-[11px] tabular-nums text-gray-600 dark:text-zinc-400">
            {props.page + 1} / {props.pageCount}
          </span>
          <button
            type="button"
            disabled={!props.canNext}
            onClick={() => props.onSetPage(props.page + 1)}
            className="rounded-md border border-gray-200 bg-white px-2 py-0.5 text-[11px] font-medium text-gray-800 disabled:opacity-40 dark:border-zinc-600 dark:bg-zinc-900 dark:text-zinc-100"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
}

export function ManufacturingInventoryAllMaterials({
  materials,
  busy,
  onEdit,
  onOpenTransactions,
  onOpenSds,
  onRequestDelete,
  onRequestRestore,
  onRequestPermanentDelete,
  selectedIds,
  onToggleSelection,
  onToggleSelectAllOnPage,
}: Props) {
  const { page, pageSize, pageCount, rangeStart, rangeEnd, pageItems, setPage, setPageSize } =
    useInventoryAllMaterialsPagination(materials);

  const canPrev = page > 0;
  const canNext = materials.length > 0 && page < pageCount - 1;
  const selectionEnabled = !!selectedIds && !!onToggleSelection;
  const pageIds = pageItems.map((m) => m.id);
  const allOnPageSelected =
    selectionEnabled && pageIds.length > 0 && pageIds.every((id) => selectedIds!.has(id));

  if (materials.length === 0) {
    return (
      <div {...{ [FIREFLY_TARGET_ATTR]: "mfg-materials-list" }} className="text-sm text-gray-500 dark:text-zinc-400">
        No matching materials.
      </div>
    );
  }

  const paginationProps: Omit<PaginationBarProps, "variant"> = {
    page,
    pageCount,
    pageSize,
    rangeStart,
    rangeEnd,
    total: materials.length,
    canPrev,
    canNext,
    onSetPage: setPage,
    onSetPageSize: setPageSize,
  };

  return (
    <div {...{ [FIREFLY_TARGET_ATTR]: "mfg-materials-list" }} className="space-y-3">
      <MaterialsPaginationBar {...paginationProps} variant="top" />
      {selectionEnabled ? (
        <div className="flex items-center gap-2 text-[11px] text-gray-600 dark:text-zinc-400">
          <input
            type="checkbox"
            checked={allOnPageSelected}
            onChange={(e) =>
              onToggleSelectAllOnPage?.(pageIds, e.target.checked)
            }
            aria-label="Select all materials on this page"
            className="h-3.5 w-3.5"
          />
          <span>Select all on this page</span>
        </div>
      ) : null}
      <ul className="divide-y divide-gray-100 dark:divide-zinc-700">
        {pageItems.map((m) => (
          <li
            key={m.id}
            className="flex flex-col gap-2 py-2.5 sm:flex-row sm:items-start sm:justify-between sm:gap-3"
          >
            <div className="flex min-w-0 flex-1 items-start gap-2">
              {selectionEnabled ? (
                <input
                  type="checkbox"
                  checked={selectedIds!.has(m.id)}
                  onChange={() => onToggleSelection!(m.id)}
                  aria-label={`Select ${m.name}`}
                  className="mt-1 h-3.5 w-3.5 shrink-0"
                />
              ) : null}
              <div className="min-w-0 flex-1 break-words">
                <div className="text-sm font-medium text-gray-900 dark:text-zinc-100">{m.name}</div>
                <div className="text-xs text-gray-600 dark:text-zinc-400">
                  {m.code} · {m.unit} · {m.category || "Uncategorized"}
                  {typeof m.metadata?.vendor === "string" && m.metadata.vendor.trim()
                    ? ` · ${m.metadata.vendor}`
                    : ""}
                </div>
                <div className="text-xs text-gray-600 dark:text-zinc-400">
                  On hand: {Number(m.on_hand_qty).toFixed(1)} · WAC: $
                  {Number(m.unit_cost).toFixed(2)}/{m.unit}
                  {(() => {
                    const reorder = reorderThresholdFromMetadata(m.metadata);
                    if (reorder == null) return "";
                    const below = Number(m.on_hand_qty) < reorder;
                    return ` · reorder ${reorder}${below ? " · below reorder" : ""}`;
                  })()}
                </div>
                {m.shipping_unit ||
                Number(m.shipping_unit_weight) > 0 ||
                Number(m.pieces_per_shipping_unit) > 1 ? (
                  <div className="text-xs text-gray-500 dark:text-zinc-500">
                    Ships as: {m.shipping_unit || "—"}
                    {Number(m.shipping_unit_weight) > 0
                      ? ` · ${Number(m.shipping_unit_weight).toFixed(1)} lbs`
                      : ""}
                    {Number(m.pieces_per_shipping_unit) > 1
                      ? ` · ${Number(m.pieces_per_shipping_unit)} ${m.unit}/${m.shipping_unit || "ship"}`
                      : ""}
                    {(() => {
                      const ppw = perPieceWeightLbs(
                        Number(m.shipping_unit_weight) || 0,
                        Number(m.pieces_per_shipping_unit) || 1
                      );
                      return ppw != null
                        ? ` · ${ppw.toFixed(2)} lbs/${m.unit || "piece"}`
                        : "";
                    })()}
                  </div>
                ) : null}
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2 sm:max-w-[min(100%,22rem)] sm:justify-end">
              <span
                className={
                  "rounded-full px-2 py-0.5 text-[11px] font-semibold " +
                  (m.is_active
                    ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200"
                    : "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200")
                }
              >
                {m.is_active ? "Active" : "Inactive"}
              </span>
              {(() => {
                const msds = parseMfgMsdsStatus(m.msds_status);
                const chipArgs = {
                  status: msds,
                  publishedVersion: m.msds_published_version,
                };
                return (
                  <span
                    className={
                      "rounded-full px-2 py-0.5 text-[11px] font-semibold " +
                      mfgMsdsStatusChipClass(chipArgs)
                    }
                    title="SDS requirement"
                  >
                    {mfgMsdsStatusChipLabel(chipArgs)}
                  </span>
                );
              })()}
              {onOpenTransactions ? (
                <button
                  type="button"
                  aria-label={`Stock transactions for ${m.name}`}
                  title={`Stock transactions for ${m.name}`}
                  className="rounded-lg border border-indigo-300 bg-white px-2 py-1 text-xs font-medium text-indigo-800 hover:bg-indigo-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-indigo-800 dark:bg-zinc-900 dark:text-indigo-200 dark:hover:bg-indigo-950/40"
                  onClick={() => onOpenTransactions(m)}
                  disabled={busy}
                >
                  Stock
                </button>
              ) : null}
              <button
                type="button"
                aria-label={`Edit ${m.name}`}
                title={`Edit ${m.name}`}
                className="rounded-lg border border-gray-300 bg-white px-2 py-1 text-xs text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-zinc-600 dark:bg-zinc-800 dark:text-zinc-200 dark:hover:bg-zinc-700"
                onClick={() => onEdit(m)}
                disabled={busy}
              >
                Edit
              </button>
              {onOpenSds && parseMfgMsdsStatus(m.msds_status) !== "not_required" ? (
                <button
                  type="button"
                  aria-label={`SDS for ${m.name}`}
                  title={`Open SDS for ${m.name}`}
                  className="rounded-lg border border-sky-300 bg-white px-2 py-1 text-xs font-medium text-sky-900 hover:bg-sky-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-sky-800 dark:bg-zinc-900 dark:text-sky-200 dark:hover:bg-sky-950/40"
                  onClick={() => onOpenSds(m)}
                  disabled={busy}
                >
                  SDS
                </button>
              ) : null}
              {m.is_active && onRequestDelete ? (
                <button
                  type="button"
                  aria-label={`Delete ${m.name}`}
                  title={`Delete ${m.name}`}
                  className="rounded-lg border border-amber-300 bg-white px-2 py-1 text-xs font-medium text-amber-800 hover:bg-amber-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-amber-800 dark:bg-zinc-900 dark:text-amber-200 dark:hover:bg-amber-950/40"
                  onClick={() => onRequestDelete(m)}
                  disabled={busy}
                >
                  Delete
                </button>
              ) : null}
              {!m.is_active && onRequestRestore ? (
                <button
                  type="button"
                  aria-label={`Restore ${m.name}`}
                  title={`Restore ${m.name}`}
                  className="rounded-lg border border-emerald-300 bg-white px-2 py-1 text-xs font-medium text-emerald-800 hover:bg-emerald-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-emerald-800 dark:bg-zinc-900 dark:text-emerald-200 dark:hover:bg-emerald-950/40"
                  onClick={() => onRequestRestore(m)}
                  disabled={busy}
                >
                  Restore
                </button>
              ) : null}
              {!m.is_active && onRequestPermanentDelete ? (
                <button
                  type="button"
                  aria-label={`Permanently delete ${m.name}`}
                  title={`Permanently delete ${m.name}`}
                  className="rounded-lg border border-red-300 bg-white px-2 py-1 text-xs font-medium text-red-800 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-red-800 dark:bg-zinc-900 dark:text-red-200 dark:hover:bg-red-950/40"
                  onClick={() => onRequestPermanentDelete(m)}
                  disabled={busy}
                >
                  Delete forever
                </button>
              ) : null}
            </div>
          </li>
        ))}
      </ul>

      <MaterialsPaginationBar {...paginationProps} variant="bottom" />
    </div>
  );
}
